import { h, defineComponent, computed } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { column } from '@/ui/components';
import { NotificationSetting, PageTypes as PageType } from '@/entities/auth/reminderConfiguration';
import { checkboxField } from '@/ui/components/form';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { isOnFreePlan, user } from '@/ui/auth/auth';
import { tooltip } from '@/ui/components/Tooltip';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { Button } from '@/ui/components/Button';

interface ConfigurationTableProps {
  list: NotificationSetting[];
  pageType: PageType;
}

export const ConfigurationTable = defineComponent<ConfigurationTableProps>({
  name: 'ConfigurationTable',
  props: ['title', 'list', 'pageType'],
  setup(props, ctx) {
    const forAction = computed(() => props.list.some(setting => setting.data.type.includes('issue')));

    const getTooltipMessage = (setting: NotificationSetting) => {
      return forAction.value ? t(`reminder-notification.tooltips.${setting.data.type}_${props.pageType}`) : t(`reminder-notification.tooltips.${setting.data.type}`);
    };

    const getTitle = (setting: NotificationSetting) => {
      return setting.data.type === 'issue_available' ? t(`reminder-notification.${setting.data.type}_${props.pageType}`) : t(`reminder-notification.${setting.data.type}`);
    };

    return function render() {
      const title = forAction.value ? t('common.actions') : t('common.checklist');

      return h(ResourceTable, {
        class: 'reminder-configurations__table',
        props: {
          titleColumnLabel: title,
          list: props.list,
          clickableTitle: false,
          sortableTitle: false,
          isHoverable: false,
        },
        scopedSlots: {
          title: ({ row: setting }: { row: NotificationSetting }) => ([
            getTitle(setting),
            tooltip({
              label: getTooltipMessage(setting),
              classes: 'reminder-configurations__tooltip',
              multilined: true,
            }, [
              featureExplanationIcon(),
            ]),
            setting.data.type === 'issue_reminder' && h(Button, {
              class: 'reminder-configurations__edit-button',
              props: {
                type: 'is-light',
              },
              on: {
                click: () => ctx.emit('openModal'),
              },
              attrs: {
                iconRight: 'pen',
                iconPack: 'far',
              },
            }),
          ]),
          default: () => [
            column({
              field: 'push',
              label: t('reminder-notification.push'),
              sortable: false,
            }, {
              default: ({ row: setting }: { row: NotificationSetting }) =>
                checkboxField({
                  name: 'push',
                  label: '',
                  value: setting.data.push,
                  required: true,
                  errors: [],
                  disabled: false,
                  hideLabel: true,
                }, v => {
                  setting.data.push = v;
                }),
            }),

            column({
              field: 'email',
              label: t('reminder-notification.email'),
              sortable: false,
            }, {
              default: ({ row: setting }: { row: NotificationSetting }) =>
                checkboxField({
                  name: 'email',
                  label: '',
                  value: setting.data.email,
                  required: true,
                  errors: [],
                  disabled: isOnFreePlan.value,
                  hideLabel: true,
                }, v => {
                  setting.data.email = v;
                }),
            }),

            user.value.organization.smsServiceEnabled && column({
              field: 'sms',
              label: t('reminder-notification.sms'),
              sortable: false,
            }, {
              default: ({ row: setting }: { row: NotificationSetting }) =>
                checkboxField({
                  name: 'sms',
                  label: '',
                  value: setting.data.sms,
                  required: true,
                  errors: [],
                  disabled: isOnFreePlan.value,
                  hideLabel: true,
                }, v => {
                  setting.data.sms = v;
                }),
            }),
          ],
        },
      });
    };
  },
});
